import type {
  AiPreferencesOnSubmitPayloads,
  AiPreferencesProps,
} from '@timelyapp/integration-cloud-sdk/components/data/ai/ai.preferences.types';
import React, { lazy, Suspense, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ticApi } from 'redux/modules/tic/api-slice/tic.apiSlice';
import { getCurrentAccountId } from 'redux/selectors/accounts';
import { getIsUserInExperimentalFeatureGroup } from 'redux/selectors/user';
import {
  getAiDerivedStatus,
  getTicAiPreferences,
} from '~domains/ai/predictions/redux/predictions.selectors';
import { useWithDispatch } from '~hooks/useWithDispatch';
import { saveAiPreferences } from '../redux/ai.preferences.actions';

// TODO: Internalize, kill from SDK
const SDK_AiPreferencesModal = lazy(
  () => import('@timelyapp/integration-cloud-sdk/components/data/ai/AiPreferencesModal')
);

type Props = Omit<AiPreferencesProps, 'timelyAccountId' | 'onSubmit' | 'derivedAiStatus'>;

const AiPreferencesModal: React.FC<Props> = props => {
  const timelyAccountId = useSelector(getCurrentAccountId);
  const initialAiPreferences = useSelector(getTicAiPreferences);
  const debugMode = useSelector(getIsUserInExperimentalFeatureGroup);
  const derivedAiStatus = useSelector(getAiDerivedStatus);
  const actions = useWithDispatch({ saveAiPreferences });

  const { onClose, onReset } = props;

  const [resetEverythingAi] = ticApi.useResetEverythingAiMutation();

  const handleSubmit = useCallback(
    (change: AiPreferencesOnSubmitPayloads) => {
      if (change.type === 'aiPreferences') {
        actions.saveAiPreferences({ preferences: change.payload });
      }
      onClose?.();
    },
    [onClose, actions]
  );

  const handleReset = useCallback(() => {
    resetEverythingAi({ body: { timely_account_id: timelyAccountId } });
    onReset?.();
    onClose?.();
  }, [onClose, onReset, resetEverythingAi, timelyAccountId]);

  return (
    <Suspense>
      <SDK_AiPreferencesModal
        debugMode={debugMode}
        timelyAccountId={timelyAccountId}
        initialPreferences={initialAiPreferences}
        derivedAiStatus={derivedAiStatus}
        {...props}
        onSubmit={handleSubmit}
        onReset={handleReset}
      />
    </Suspense>
  );
};

export default AiPreferencesModal;
